import { Box, Paper, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { SystemStackedBar } from "../../../../components/UIToolkit/DataViz/SystemStackedBar/SystemStackedBar";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { FONT_SIZE } from "../../../../theme/tokens";
import { type BuildingFigures, REVIEW_BUCKET_HOURS } from "../../network/networkMath";
import { sectionCardSx } from "../../PredictionTab/colors";
import type { OpenAuditDestination } from "../auditSubTabs";
import { REVIEW_BUCKET_VIEWS } from "./reviewBuckets";
import type { SummaryFormatters } from "./useSummaryFormatters";

const LEGEND_DOT_PX = 10;
const HERO_BAR_HEIGHT_PX = 12;
/** Narrowest a legend entry gets before the legend drops a column, so a label never runs off the card. */
const LEGEND_ITEM_MIN_WIDTH_PX = 240;

type Props = {
	totals: BuildingFigures;
	reviewHours: number;
	/** Share of hours with nothing to review; null when there are no hours at all. */
	clean: number | null;
	formatters: SummaryFormatters;
	onOpen: OpenAuditDestination;
};

/** The Summary's lead: hours to look at before filing, what makes them up, and a link from each part to its list. */
export const ReviewHoursCard: FC<Props> = ({ totals, reviewHours, clean, formatters: { formatHours, formatPercent }, onOpen }) => {
	const { t } = useTranslation();
	const buckets = REVIEW_BUCKET_VIEWS.map((view) => ({ ...view, hours: REVIEW_BUCKET_HOURS[view.bucket](totals), label: t(view.labelKey) }));
	return (
		<Paper elevation={0} sx={(theme) => ({ ...sectionCardSx(theme), p: 2.5, flex: 2, minWidth: 0 })} data-testid="pbj-network-summary-review">
			<Typography variant="overline" color="text.secondary">
				{t("pbj_model.network.summary.review.title")}
			</Typography>
			<Stack direction="row" spacing={1.5} sx={{ alignItems: "baseline", mb: 1.5 }}>
				<Typography variant="h3" component="p" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }} data-testid="pbj-network-summary-review-hours">
					{formatHours(reviewHours)}
				</Typography>
				<Typography variant="body2" color="text.secondary">
					{clean === null
						? t("pbj_model.network.summary.review.no_hours")
						: t("pbj_model.network.summary.review.of_total", { total: formatHours(totals.totalHours), share: formatPercent(1 - clean) })}
				</Typography>
			</Stack>
			<SystemStackedBar
				segments={buckets.map((bucket) => ({ id: bucket.bucket, value: bucket.hours, color: bucket.color }))}
				height={HERO_BAR_HEIGHT_PX}
				ariaLabel={buckets.map((bucket) => `${bucket.label}: ${formatHours(bucket.hours)}`).join(", ")}
				dataTestId="pbj-network-summary-review-bar"
			/>
			<Box sx={{ display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${LEGEND_ITEM_MIN_WIDTH_PX}px), 1fr))`, columnGap: 2, mt: 1.5 }}>
				{buckets.map((bucket) => (
					<SystemButton
						key={bucket.bucket}
						buttonVariant="borderless"
						size="small"
						noFullWidthMobile
						onClick={() => onOpen(bucket.destination)}
						data-testid={`pbj-network-summary-bucket-${bucket.bucket}`}
						// Responsive width on purpose: SystemButton sets its own width per breakpoint, which a plain
						// `width` can't outrank. Filling the column lets a long label truncate instead of overflowing.
						sx={{ justifyContent: "space-between", gap: 1, width: { xs: "100%", sm: "100%" }, minWidth: 0 }}
					>
						<Stack direction="row" spacing={1} sx={{ alignItems: "center", minWidth: 0 }}>
							<Box sx={{ width: LEGEND_DOT_PX, height: LEGEND_DOT_PX, borderRadius: 0.5, flexShrink: 0, backgroundColor: bucket.color }} />
							<Typography variant="body2" noWrap title={bucket.label} sx={{ textAlign: "start" }}>
								{bucket.label}
							</Typography>
						</Stack>
						<Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
							{t("pbj_model.network.summary.hours_unit", { hours: formatHours(bucket.hours) })}
						</Typography>
					</SystemButton>
				))}
			</Box>
			<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, fontSize: FONT_SIZE.SECONDARY }}>
				{t("pbj_model.network.summary.review.missed_punch_note")}
			</Typography>
		</Paper>
	);
};
